import { AdminTableContainer, Button } from "@simplicity/ui";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import type { RunsReportResponse } from "../types";
import type { ModeFilter } from "./RunsTable.utils";
import {
  buildFilterParts,
  filterByConnectorRunId,
  filterRunsByMode,
  getDateKey,
  groupByProject,
  parseModeFilter,
} from "./RunsTable.utils";
import { ProjectRow } from "./RunsTable/ProjectRow";
import { RunsTruncatedNotice } from "./RunsTruncatedNotice";

// SIM-1452: migrated to <Button variant="chip"> with aria-pressed for active.
// `.mode-filter-btn` CSS rule deleted from index.css in the same commit.
function ModeFilterButtons({
  filterMode,
  setMode,
}: {
  filterMode: ModeFilter;
  setMode: (mode: "all" | "full" | "incremental") => void;
}) {
  return (
    <div className="inline-flex w-fit items-center gap-1.5">
      <Button variant="chip" aria-pressed={!filterMode} onClick={() => setMode("all")}>
        All
      </Button>
      <Button variant="chip" aria-pressed={filterMode === "full"} onClick={() => setMode("full")}>
        Full
      </Button>
      <Button
        variant="chip"
        aria-pressed={filterMode === "incremental"}
        onClick={() => setMode("incremental")}
      >
        Incremental
      </Button>
    </div>
  );
}

function FilterBar({ parts }: { parts: string[] }) {
  if (parts.length === 0) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">Filtered by:</span>
      {parts.map((part, i) => (
        <span key={i} className="filter-tag">
          {part}
        </span>
      ))}
      <Link
        to="/detail"
        className="clear-filters ml-2 text-xs text-muted-foreground no-underline hover:text-destructive"
      >
        Clear
      </Link>
    </div>
  );
}

function useRunsFilters(data: RunsReportResponse | null) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterProjectId = searchParams.get("projectId");
  const filterDate = searchParams.get("date");
  const filterMode = parseModeFilter(searchParams.get("mode"));
  const connectorRunIdParam = searchParams.get("connectorRunId");
  const connectorRunId = connectorRunIdParam ? Number(connectorRunIdParam) : null;

  const setMode = (mode: "all" | "full" | "incremental") => {
    const next = new URLSearchParams(searchParams);
    if (mode === "all") next.delete("mode");
    else next.set("mode", mode);
    setSearchParams(next);
  };

  const groupedRuns = useMemo(() => {
    if (!data) return [];
    let runs = [...data.runs];
    if (filterProjectId) runs = runs.filter((r) => r.projectId === filterProjectId);
    if (filterDate) runs = runs.filter((r) => getDateKey(r.startedAt) === filterDate);
    if (connectorRunId && Number.isFinite(connectorRunId)) {
      runs = filterByConnectorRunId(runs, connectorRunId);
    }
    runs = filterRunsByMode(runs, filterMode);
    return groupByProject(runs);
  }, [data, filterProjectId, filterDate, filterMode, connectorRunId]);

  const filterParts = useMemo(() => {
    if (!data) return [];
    return buildFilterParts(data, filterProjectId, filterDate, filterMode, connectorRunId);
  }, [data, filterProjectId, filterDate, filterMode, connectorRunId]);

  return {
    filterProjectId,
    filterMode,
    connectorRunId,
    setMode,
    groupedRuns,
    filterParts,
  };
}

export function RunsTable({
  data,
  loading,
  error,
}: {
  data: RunsReportResponse | null;
  loading: boolean;
  error: string | null;
}) {
  const { filterProjectId, filterMode, connectorRunId, setMode, groupedRuns, filterParts } =
    useRunsFilters(data);

  if (loading) {
    return (
      <div className="page-view empty-state max-w-[1600px] font-mono">
        <div className="dv-spinner" />
        <p>Loading runs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-view empty-state max-w-[1600px] font-mono">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  return (
    <div className="page-view max-w-[1600px] font-mono">
      <div className="section-header items-start">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl">Runs Detail</h2>
          <ModeFilterButtons filterMode={filterMode} setMode={setMode} />
          <FilterBar parts={filterParts} />
        </div>
        <Link to="/" className="back-link">
          ← Back to Overview
        </Link>
      </div>
      <RunsTruncatedNotice data={data} />

      <AdminTableContainer aria-label="Runs detail">
        <table className="admin-table detail-table">
          <thead>
            <tr>
              <th className="th-name">Name</th>
              <th>Day</th>
              <th>Started</th>
              <th>Pages</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Mode</th>
              <th>Count</th>
              <th>Inserted</th>
              <th>Fetched</th>
              <th>Skipped</th>
              <th className="th-source">Source File</th>
            </tr>
          </thead>
          <tbody>
            {groupedRuns.length === 0 ? (
              <tr>
                <td colSpan={12} className="no-data">
                  No runs found
                </td>
              </tr>
            ) : (
              groupedRuns.flatMap((project, idx) => [
                idx === 0 ? null : (
                  <tr key={`spacer-${project.projectId}`} className="project-day-spacer">
                    <td colSpan={12} />
                  </tr>
                ),
                <ProjectRow
                  key={project.projectId}
                  project={project}
                  autoExpandConnectorId={connectorRunId}
                  defaultExpanded={!!filterProjectId || !!connectorRunId}
                />,
              ])
            )}
          </tbody>
        </table>
      </AdminTableContainer>
    </div>
  );
}
